import { mapLifecycleTypeToLifecycleLabel } from 'util/mapLifecycleTypeToLifecycleLabel';

import { ClientRoute } from 'app/route/client';
import { FeatureFlags } from 'config/featureFlags';
import { growthbook } from 'config/growthbook';
import { LifecycleType } from 'model/Lifecycle';
import { createLoadingNode, NavigationNode } from 'model/NavigationNode';
import { ProjectModel } from 'api/dto/Project.dto';
import { compilePath } from 'router/compilePath';

const pickLegacyLifecycleProjects = (
  pulseLifecycleProjects: ProjectModel[],
  isMultipleOnboardSurveysEnabled: boolean,
): ProjectModel[] => {
  if (isMultipleOnboardSurveysEnabled) {
    return pulseLifecycleProjects.filter(
      (project) => project.lifecycleType !== LifecycleType.ONBOARD,
    );
  }

  // Sort by id (auto-incrementing) to align with sequenceNumber === 1 used
  // by pickVisibleSurveys in OnboardSurveysPage. The lowest project id
  // corresponds to the first-created onboard survey (sequenceNumber 1).
  const onboardProjects = pulseLifecycleProjects
    .filter((project) => project.lifecycleType === LifecycleType.ONBOARD)
    .sort((a, b) => a.id - b.id);
  const firstOnboard = onboardProjects[0];

  return pulseLifecycleProjects.filter(
    (project) => project.lifecycleType !== LifecycleType.ONBOARD || project === firstOnboard,
  );
};

const lifecycleSurveysGroup = (children: NavigationNode[]): NavigationNode => ({
  id: 'employeeLifecycleSurveys',
  label: 'Employee Lifecycle Surveys',
  children,
});

export const generateLifecycleSurveysLinks = (
  id: number,
  pulseLifecycleProjects: ProjectModel[],
  isLifecycleSurveysEnabled: boolean,
  isPulseTemplateFullyLoaded: boolean,
): NavigationNode[] => {
  const isMultipleOnboardSurveysEnabled =
    growthbook.isOn(FeatureFlags.MULTIPLE_ONBOARD_SURVEYS) && isLifecycleSurveysEnabled;

  const legacyLifecycleEntries = pickLegacyLifecycleProjects(
    pulseLifecycleProjects,
    isMultipleOnboardSurveysEnabled,
  ).map((project) => ({
    id: project.lifecycleType,
    key: `lifecycle-${project.id}`,
    lifecycleType: project.lifecycleType,
    label: mapLifecycleTypeToLifecycleLabel(project.lifecycleType),
    href: compilePath(ClientRoute.Project.Survey.Develop.QUESTION_PATTERN, {
      id: id,
      survey_id: project.id,
    }),
    pattern: ClientRoute.Project.Survey.Develop.QUESTION_PATTERN,
  }));

  const newOnboardEntry: NavigationNode[] = isMultipleOnboardSurveysEnabled
    ? [
        {
          id: 'onboardSurveys',
          label: 'Onboard Surveys',
          href: compilePath(ClientRoute.Project.LifecycleSurveys.ONBOARD, { id }),
          pattern: ClientRoute.Project.LifecycleSurveys.ONBOARD,
        },
      ]
    : [];

  if (!isPulseTemplateFullyLoaded) {
    return isLifecycleSurveysEnabled
      ? [
          lifecycleSurveysGroup([
            ...newOnboardEntry,
            createLoadingNode(
              'employeeLifecycleSurveysLoader',
              'Loading lifecycle surveys',
              [ClientRoute.Project.Survey.Develop.QUESTION_PATTERN],
              { isForLifecycleSurveys: true },
            ),
          ]),
        ]
      : [];
  }

  const children = [...newOnboardEntry, ...legacyLifecycleEntries];

  return children.length > 0 ? [lifecycleSurveysGroup(children)] : [];
};
